import React, { useEffect, useReducer, useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery, useMutation } from "@apollo/client";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { toast } from "react-toastify";

// Custom.
import Layout from "containers/Layout/Layout";
import { VoLoader, VoTextField } from "@vocollege/components";
import { FormTabs } from "@vocollege/components";
import I18n from "@vocollege/app/dist/modules/Services/I18n";
import {
  GET_SETTINGS,
  UPDATE_SETTINGS,
} from "@vocollege/app/dist/modules/VoApi/graphql";
import { useStyles } from "./styles";
import { FormToolbar } from "@vocollege/components";
import { reducer, initialState } from "./state";
import EntityField from "@vocollege/components/dist/Form/fields/EntityField";
import { Setting } from "./global";

const Settings = () => {
  const classes = useStyles();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [loading, setLoading] = useState(true);
  const [tabs, setTabs] = useState<{ [key: string]: any }[]>([]);
  const [currentTab, setTab] = useState(0);
  const {
    handleSubmit,
    register,
    setValue,
    reset,
    formState,
    // getValues,
  } = useForm({
    mode: "onChange",
  });

  const { isDirty, isValid } = formState;

  // API.

  const { error, data } = useQuery(GET_SETTINGS, {
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });

  const [update, { loading: updateLoading }] = useMutation(UPDATE_SETTINGS, {
    onError: (error) => {
      toast.error(error.message);
    },
    onCompleted: (data) => {
      toast.success(I18n.get.settings.messages.settingsSaved);
      bindFieldValidation();
      setData(data.updateSettings);
    },
  });

  // Methods.

  const getInputValues = () => {
    const result = [];
    for (let section in state) {
      switch (state[section].category) {
        case "images":
          if (state[section].value.length > 0) {
            result.push({
              name: state[section].name,
              category: state[section].category,
              value: state[section].value[0].id,
            });
          }
          break;
        default:
          if (state[section].value !== "") {
            result.push({
              name: state[section].name,
              category: state[section].category,
              value: state[section].value,
            });
          }

          break;
      }
    }

    return result;
  };

  const generateTabs = (settings: Setting[]) => {
    const tabGroups: { [key: string]: any }[] = [];
    Object.keys(initialState).forEach((v: string) => {
      let i = tabGroups.findIndex(
        (tabGroup: any) => tabGroup.label === initialState[v].category,
      );
      if (i === -1) {
        tabGroups.push({
          label: initialState[v].category?.toLowerCase(),
        });
      }
    });
    setTabs(tabGroups);
  };

  const handleCancel = () => {
    setData(data.settings);
  };

  const handleSave = () => {
    const variables: { [key: string]: any } = {
      settings: getInputValues(),
    };
    update({
      variables: variables,
    });
  };

  const handleEntityFieldChange = (
    name: string,
    category: string,
    value: string,
  ) => {
    dispatch({
      field: name,
      value: {
        name,
        category,
        value,
      },
    });
    setValue(name, value, { shouldValidate: true, shouldDirty: true });
  };

  const setData = (data: Setting[]) => {
    const parsedData: { [key: string]: any } = {};
    data.forEach((v: Setting) => {
      setValue(v.name, v.value);
      parsedData[v.name] = { ...v };
    });
    dispatch({
      item: { ...parsedData },
    });
  };

  const bindFieldValidation = () => {
    reset();
    for (let field in initialState) {
      register(field);
    }
  };

  // Effects.

  useEffect(() => {
    bindFieldValidation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data && data.settings) {
      setData(
        data.settings.map((v: Setting) => {
          if (v.category === "images") {
            return {
              name: v.name,
              category: v.category,
              value: v.file,
            };
          } else {
            return v;
          }
        }),
      );
      generateTabs(data.settings);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (error) {
    toast.error(error.message, { autoClose: false });
  }

  return (
    <Layout>
      {(loading || updateLoading) && <VoLoader />}
      {!loading && error && <div>{JSON.stringify(error)}</div>}
      {!loading && !error && (
        <form className={classes.root} noValidate autoComplete="off">
          <FormToolbar
            title={I18n.get.misc.settings}
            onSave={handleSubmit(handleSave)}
            onCancel={handleCancel}
            loading={updateLoading}
            options={{
              saveButton: {
                disabled: !isDirty || !isValid,
              },
            }}
            className="vo-global__content-toolbar"
          />
          <Paper elevation={10} className="vo-global__content-with-toolbar">
            {/* <div>{JSON.stringify(getValues())}</div> */}
            {/* <div>{JSON.stringify(state)}</div> */}
            {/* <div>{JSON.stringify(formState)}</div> */}
            <Grid container spacing={2}>
              {tabs.length > 0 && (
                <Grid item xs={12}>
                  <FormTabs
                    tabs={tabs}
                    currentTab={currentTab}
                    setTab={setTab}
                  ></FormTabs>
                </Grid>
              )}

              {currentTab === 0 && (
                <>
                  <Grid item xs={12}>
                    <EntityField
                      name="slideshowQueue"
                      label={I18n.get.settings.label.slideshowQueue}
                      value={state.slideshowQueue.value}
                      onChange={(item) =>
                        handleEntityFieldChange(
                          "slideshowQueue",
                          "front",
                          item.id,
                        )
                      }
                      onReset={() =>
                        handleEntityFieldChange("slideshowQueue", "front", "")
                      }
                      required={true}
                      dialog={{ types: ["queue"] }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <EntityField
                      name="newsQueue"
                      label={I18n.get.settings.label.newsQueue}
                      value={state.newsQueue.value}
                      onChange={(item) =>
                        handleEntityFieldChange("newsQueue", "front", item.id)
                      }
                      onReset={() =>
                        handleEntityFieldChange("newsQueue", "front", "")
                      }
                      required={true}
                      dialog={{ types: ["queue"] }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <EntityField
                      name="puffQueue"
                      label={I18n.get.settings.label.puffQueue}
                      value={state.puffQueue.value}
                      onChange={(item) =>
                        handleEntityFieldChange("puffQueue", "front", item.id)
                      }
                      onReset={() =>
                        handleEntityFieldChange("puffQueue", "front", "")
                      }
                      required={true}
                      dialog={{ types: ["queue"] }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <EntityField
                      name="sectionsQueue"
                      label={I18n.get.settings.label.sectionsQueue}
                      value={state.sectionsQueue.value}
                      onChange={(item) =>
                        handleEntityFieldChange(
                          "sectionsQueue",
                          "front",
                          item.id,
                        )
                      }
                      onReset={() =>
                        handleEntityFieldChange("sectionsQueue", "front", "")
                      }
                      required={true}
                      dialog={{ types: ["queue"] }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <EntityField
                      name="mainMenu"
                      label={I18n.get.settings.label.mainMenu}
                      value={state.mainMenu.value}
                      onChange={(item) =>
                        handleEntityFieldChange("mainMenu", "front", item.id)
                      }
                      onReset={() =>
                        handleEntityFieldChange("mainMenu", "front", "")
                      }
                      required={true}
                      dialog={{ types: ["menu"], primaryField: "name" }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <EntityField
                      name="headerMenu"
                      label={I18n.get.settings.label.headerMenu}
                      value={state.headerMenu.value}
                      onChange={(item) =>
                        handleEntityFieldChange("headerMenu", "front", item.id)
                      }
                      onReset={() =>
                        handleEntityFieldChange("headerMenu", "front", "")
                      }
                      required={true}
                      dialog={{ types: ["menu"], primaryField: "name" }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <EntityField
                      name="footerMenu"
                      label={I18n.get.settings.label.footerMenu}
                      value={state.footerMenu.value}
                      onChange={(item) =>
                        handleEntityFieldChange("footerMenu", "front", item.id)
                      }
                      onReset={() =>
                        handleEntityFieldChange("footerMenu", "front", "")
                      }
                      required={true}
                      dialog={{ types: ["menu"], primaryField: "name" }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <EntityField
                      name="collegePage"
                      label={I18n.get.settings.label.collegePage}
                      value={state.collegePage.value}
                      onChange={(item) =>
                        handleEntityFieldChange("collegePage", "front", item.id)
                      }
                      onReset={() =>
                        handleEntityFieldChange("collegePage", "front", "")
                      }
                      required={true}
                      dialog={{ types: ["page"] }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <EntityField
                      name="publicDefaultPortfolio"
                      label={I18n.get.settings.label.publicDefaultPortfolio}
                      value={state.publicDefaultPortfolio.value}
                      onChange={(item) =>
                        handleEntityFieldChange(
                          "publicDefaultPortfolio",
                          "front",
                          item.id,
                        )
                      }
                      onReset={() =>
                        handleEntityFieldChange(
                          "publicDefaultPortfolio",
                          "front",
                          "",
                        )
                      }
                      required={true}
                      dialog={{ types: ["portfolio"] }}
                    />
                  </Grid>
                </>
              )}

              {currentTab === 1 && (
                <>
                  <Grid item xs={12}>
                    <EntityField
                      name="validigDefaultPortfolio"
                      label={I18n.get.settings.label.validigDefaultPortfolio}
                      value={state.validigDefaultPortfolio.value}
                      onChange={(item) =>
                        handleEntityFieldChange(
                          "validigDefaultPortfolio",
                          "front",
                          item.id,
                        )
                      }
                      onReset={() =>
                        handleEntityFieldChange(
                          "validigDefaultPortfolio",
                          "front",
                          "",
                        )
                      }
                      required={true}
                      dialog={{ types: ["portfolio"] }}
                    />
                  </Grid>
                </>
              )}
              {currentTab === 2 && (
                <>
                  <Grid item xs={12}>
                    <EntityField
                      name="vocertDefaultHelpBook"
                      label={I18n.get.settings.label.vocertDefaultHelpBook}
                      value={state.vocertDefaultHelpBook.value}
                      onChange={(item) =>
                        handleEntityFieldChange(
                          "vocertDefaultHelpBook",
                          "vocert",
                          item.id,
                        )
                      }
                      onReset={() =>
                        handleEntityFieldChange(
                          "vocertDefaultHelpBook",
                          "vocert",
                          "",
                        )
                      }
                      // required={true}
                      dialog={{ types: ["book"] }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <VoTextField
                      name="vocertCourseMomentBillingMail"
                      label={
                        I18n.get.settings.label.vocertCourseMomentBillingMail
                      }
                      value={state.vocertCourseMomentBillingMail.value}
                      variant="filled"
                      fullWidth
                      onChange={(e) =>
                        handleEntityFieldChange(
                          "vocertCourseMomentBillingMail",
                          "vocert",
                          e.target.value,
                        )
                      }
                    />
                  </Grid>
                </>
              )}
              {currentTab === 3 && (
                <>
                  <Grid item xs={12}>
                    <EntityField
                      name="voadminDefaultHelpBook"
                      label={I18n.get.settings.label.voadminDefaultHelpBook}
                      value={state.voadminDefaultHelpBook.value}
                      onChange={(item) =>
                        handleEntityFieldChange(
                          "voadminDefaultHelpBook",
                          "voadmin",
                          item.id,
                        )
                      }
                      onReset={() =>
                        handleEntityFieldChange(
                          "voadminDefaultHelpBook",
                          "voadmin",
                          "",
                        )
                      }
                      dialog={{ types: ["book"] }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>
        </form>
      )}
    </Layout>
  );
};

export default Settings;
